import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { PlanService } from '../../services/plan.service';
import { Plan } from '../../model/plan.model';
import { ModalController } from '@ionic/angular/standalone';
import { IonHeader, IonToolbar, IonBackButton, IonTitle, IonContent, IonSelect, IonSelectOption, IonIcon } from '@ionic/angular/standalone';
import { PlanFeatureQuota } from '../../model/plan-feature-quota.model';
import { DisplayService, ValidationUtil } from '@qid/core';
import { PlanStatus } from '../../enum/plan-status.enum';
import { PlanType } from '../../enum/plan-type.enum';
import { PlanUser } from '../../enum/plan-user.enum';
import { PlanPricingModel } from '../../model/plan-pricing-model.model';
import { Feature, FeatureService, ListFeatureComponent } from '../../../../feature';
import { PlanVisibility } from '../../enum/plan-visibility.enum';
import { Location } from '@angular/common';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SexyModule } from '@qid/sexy';
import { SuperButtonComponent, SuperInputComponent, PageActionComponent } from '@qid/super';

@Component({
  selector: 'app-view-plan',
  templateUrl: './view-plan.page.html',
  styleUrls: ['./view-plan.page.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    SexyModule,
    SuperButtonComponent,
    SuperInputComponent,
    PageActionComponent,
    IonHeader,
    IonToolbar,
    IonBackButton,
    IonTitle,
    IonContent,
    IonSelect,
    IonSelectOption,
    IonIcon
  ]
})
export class ViewPlanPage implements OnInit {

  public plan: Plan = new Plan();
  public planStatusOptions = Object.values(PlanStatus);
  public planTypeOptions = Object.values(PlanType);
  public planVisibilityOptions = Object.values(PlanVisibility);
  public planUserOptions = Object.values(PlanUser);

  public features: Feature[] = [];

  constructor(private activatedRoute: ActivatedRoute,
    private planService: PlanService,
    private modalController: ModalController,
    private displayService: DisplayService,
    private featureService: FeatureService,
    private location: Location,
  ) {
  }

  ngOnInit() {
    const id = this.activatedRoute.snapshot.paramMap.get('id');
    this.fetchPlan(id);
    this.fetchFeatures();
  }

  private async fetchPlan(id: string) {
    const res = await this.planService.readOne(id);
    if (res) {
      this.plan = res.data;
    }

  }

  private async fetchFeatures() {
    this.features = await this.featureService.readAll();
  }

  async onAddPricingModelClicked() {
    this.plan.planPricingModels.push(new PlanPricingModel());
  }

  async onAddFeatureClicked() {
    const modal = await this.modalController.create({
      component: ListFeatureComponent,
      componentProps: {
        showEditOptions: false,
        showAddButton: true,
        onAddCallback: (feature: Feature) => {
          this.addFeature(feature);
        }
      }
    });
    modal.present();
  }

  public async onDeletePricingModel(deleteIndex: number) {
    this.plan.planPricingModels.splice(deleteIndex, 1);
  }

  public async onDeleteFeature(planFeatureQuota: PlanFeatureQuota) {
    if (!planFeatureQuota) {
      return;
    }
    const { featureId } = planFeatureQuota;
    this.plan.planFeatureQuota = this.plan.planFeatureQuota.filter(item => item.featureId !== featureId);

  }


  private async addFeature(feature: Feature) {
    if (this.isFeatureAdded(feature)) {
      return this.displayService.toast({ message: 'Feature already added', duration: 1000, position: 'top' });
    }

    this.plan.planFeatureQuota.push(new PlanFeatureQuota(feature.keyName, feature.defaultQuota));
  }

  private isFeatureAdded(feature: Feature): boolean {
    for (let planFeature of this.plan.planFeatureQuota) {
      if (planFeature.featureKeyName == feature.keyName) {
        return true;
      }
    }
    return false;
  }

  checkFields() {
    if (ValidationUtil.isStringEmpty(this.plan.status)) {
      return { message: 'Plan status required', value: true };
    } else if (ValidationUtil.isStringEmpty(this.plan.type)) {
      return { message: 'Plan type required', value: true };
    } else if (ValidationUtil.isStringEmpty(this.plan.visibility)) {
      return { message: 'Plan visibility required', value: true };
    } else if (ValidationUtil.isStringEmpty(this.plan.planUser)) {
      return { message: 'plan user required', value: true };
    } else if (this.plan.planFeatureQuota.length < 1) {
      return { message: 'Plan features required', value: true };
    } else if (this.plan.planPricingModels.length < 1) {
      return { message: 'Plan prices can\'t be empty', value: true };
    } else if (this.plan.planPricingModels.length >= 1) {
      for (let item of this.plan.planPricingModels) {
        if (ValidationUtil.isStringEmpty(item.name)) {
          return { message: 'Plan name is required', value: true };
        } else if (!item.amount) {
          return { message: 'Plan amount required', value: true };
        } else if (!item.validityDays) {
          return { message: 'Plan days required', value: true };
        }
      }
    }
    return { message: '', value: false };
  }

  public updatePlan = async () => {
    if (this.checkFields().value) {
      return this.displayService.toast({ message: this.checkFields().message, color: 'warning' });
    }

    const res = await this.planService.update(this.plan);
    if (res && res.data) {
      await this.displayService.toast({ message: 'Plan Updated', duration: 1000, position: 'top', color: 'success' });
    }
  };

  public deletePlan = async () => {
    const res = await this.planService.delete(this.plan);
    if (res && res.data) {
      this.location.back();
      await this.displayService.toast({ message: `${this.plan.name} Deleted`, duration: 1000, position: 'top', color: 'success' });
    }
  };

  getFeatureByKeyName(keyName: string): Feature {
    for (let feature of this.features) {
      if (feature.keyName == keyName) {
        return feature;
      }
    }
    return null;
  }
}
